import { useEffect, useState } from 'react';
import useLocalStorage from './useLocalStorage';

const MAX_PROMPTS = 3;

/**
 * Hook to manage location permission state
 * Tracks: permission status, prompt count, and user preference
 */
export default function useLocationPermission() {
  const [locationPreference, setLocationPreference] = useLocalStorage(
    'locationPreference',
    {
      status: 'unknown', // 'unknown' | 'granted' | 'denied' | 'maybe-later'
      promptCount: 0,
      lastPromptDate: null,
    }
  );

  const [browserPermission, setBrowserPermission] = useState('prompt');

  // Check browser permission status
  useEffect(() => {
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions
        .query({ name: 'geolocation' })
        .then((result) => {
          setBrowserPermission(result.state);

          // Listen for permission changes
          result.addEventListener('change', () => {
            setBrowserPermission(result.state);
          });
        })
        .catch(() => {
          // Permissions API not supported, fallback to checking geolocation
          setBrowserPermission('prompt');
        });
    }
  }, []);

  // Sync browser permission with localStorage
  useEffect(() => {
    if (
      browserPermission === 'granted' &&
      locationPreference.status !== 'granted'
    ) {
      setLocationPreference((prev) => ({ ...prev, status: 'granted' }));
    } else if (
      browserPermission === 'denied' &&
      locationPreference.status !== 'denied'
    ) {
      setLocationPreference((prev) => ({ ...prev, status: 'denied' }));
    }
  }, [browserPermission, locationPreference.status, setLocationPreference]);

  const shouldPrompt = () => {
    // Don't prompt if user has made a definitive choice
    if (
      locationPreference.status === 'granted' ||
      locationPreference.status === 'denied'
    ) {
      return false;
    }

    // Don't prompt if we've already asked 3 times
    if (locationPreference.promptCount >= MAX_PROMPTS) {
      return false;
    }

    return true;
  };

  const incrementPromptCount = () => {
    const newCount = locationPreference.promptCount + 1;
    setLocationPreference({
      ...locationPreference,
      promptCount: newCount,
      lastPromptDate: new Date().toISOString(),
    });
  };

  const setPermissionGranted = () => {
    setLocationPreference({
      ...locationPreference,
      status: 'granted',
      lastPromptDate: new Date().toISOString(),
    });
  };

  const setPermissionDenied = () => {
    setLocationPreference({
      ...locationPreference,
      status: 'denied',
      promptCount: MAX_PROMPTS, // Mark as max so we don't ask again
      lastPromptDate: new Date().toISOString(),
    });
  };

  const setMaybeLater = () => {
    // Don't increment count here - it's already incremented when modal is shown
    const currentCount = locationPreference.promptCount;
    setLocationPreference({
      ...locationPreference,
      status: currentCount >= MAX_PROMPTS ? 'denied' : 'maybe-later',
      lastPromptDate: new Date().toISOString(),
    });
  };

  const canUseLocation = () =>
    browserPermission === 'granted' && locationPreference.status === 'granted';

  const isLocationDenied = () => {
    // Only disable if browser explicitly denied OR user clicked "Do Not Allow"
    // Don't disable for "maybe-later" or "unknown" - let them try
    if (browserPermission === 'granted') {
      return false; // Browser allowed, always enable button
    }
    return (
      browserPermission === 'denied' || locationPreference.status === 'denied'
    );
  };

  const hasResponded = () =>
    // User has responded if they've granted, denied, or clicked maybe later
    locationPreference.status !== 'unknown';
  return {
    locationPreference,
    browserPermission,
    shouldPrompt,
    incrementPromptCount,
    setPermissionGranted,
    setPermissionDenied,
    setMaybeLater,
    canUseLocation,
    isLocationDenied,
    hasResponded,
    // Third prompt is when count is 2 (we've shown it twice already: count 0 and 1)
    // Count 0 = 1st prompt, Count 1 = 2nd prompt, Count 2 = 3rd prompt
    isThirdPrompt: locationPreference.promptCount >= MAX_PROMPTS - 1,
  };
}
